// The route of a REPLAY, worked out in the browser as the day plays: the same walk as the server's (insights/pathCore.ts), fed the visits that have ENDED so far in the order each went through its pages.
// A visit still on the site is not counted, as in the Insights report (a visitor onsite is a visitor, not yet a session). Pure: ids and page names in, an answer out.
import { PathAccumulator, PathAnswer, RouteCount, ROUTE_SEP } from "../insights/pathCore";

export interface RouteTally {
  arrive(id: string, page: string): void;
  move(id: string, page: string): void;
  leave(id: string): void;
  reset(): void;
  answer(): PathAnswer;
  /** the tally as a flat map (see PathAccumulator.routes) */
  routes(): Record<string, RouteCount>;
  /** start from a tally kept elsewhere (the day before a clip): replaces what is counted; visits still onsite are forgotten */
  seed(routes: Record<string, RouteCount>): void;
}

/** a route's tally as it travels in a clip: the stops of a route so far, visits that went that way, visits that ended there, pages viewed on its last stop */
export type WireRoute = [string[], number, number, number];

/** the busiest routes of a tally, parents before their children (a child is never busier than its parent, so keeping the busiest keeps every route whole), at most `max` */
export const routesToWire = (routes: Record<string, RouteCount>, max: number): WireRoute[] =>
  Object.entries(routes)
    .map(([key, c]) => ({ stops: key.split(ROUTE_SEP), c }))
    .sort((a, b) => b.c.n - a.c.n || a.stops.length - b.stops.length)
    .slice(0, max)
    .map(({ stops, c }): WireRoute => [stops, c.n, c.ended, c.runSum]);

export const routesFromWire = (rows: WireRoute[] | null | undefined): Record<string, RouteCount> => {
  const out: Record<string, RouteCount> = {};
  for (const row of rows || []) {
    if (!Array.isArray(row) || !Array.isArray(row[0]) || row[0].length === 0) continue;
    out[row[0].join(ROUTE_SEP)] = { n: Number(row[1]) || 0, ended: Number(row[2]) || 0, runSum: Number(row[3]) || 0 };
  }

  return out;
};

export const createRouteTally = (): RouteTally => {
  let acc = new PathAccumulator();
  const onsite = new Map<string, string[]>();

  return {
    arrive: (id, page) => { onsite.set(id, page ? [page] : []); },
    move: (id, page) => { const pages = onsite.get(id); if (pages && page) pages.push(page); },
    leave: (id) => { const pages = onsite.get(id); if (pages) { acc.add(pages); onsite.delete(id); } },
    reset: () => { acc = new PathAccumulator(); onsite.clear(); },
    answer: () => acc.answer(),
    routes: () => acc.routes(),
    seed: (routes) => { acc = PathAccumulator.fromRoutes(routes); onsite.clear(); },
  };
};
